import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Plus, Trash2, Edit, ArrowLeft, MapPin } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ZipRouting() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [routings, setRoutings] = useState([]);
  const [techs, setTechs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    zip_code: "",
    primary_tech_id: "",
    fallback_tech_id: "",
    self_book_enabled: true,
  });

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAuthenticated(true);
          setUser(response.data);
        } else {
          setIsAuthenticated(false);
          navigate('/login');
        }
      } catch (error) {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  useEffect(() => {
    if (isAuthenticated) {
      if (user?.role !== "Admin") {
        navigate('/settings');
        return;
      }
      fetchRoutings();
      fetchTechs();
    }
  }, [isAuthenticated, user]);

  const fetchRoutings = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/zip-routing`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setRoutings(response.data);
    } catch (error) {
      toast.error("Failed to fetch zip routing");
    }
  };

  const fetchTechs = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/techs`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setTechs(response.data);
    } catch (error) {
      toast.error("Failed to fetch techs");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('session_token');
      const submitData = {
        ...formData,
        // Send "none" explicitly when no fallback is selected
        fallback_tech_id: formData.fallback_tech_id || "none"
      };

      if (editingId) {
        await axios.put(`${API}/zip-routing/${editingId}`, submitData, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success("Zip routing updated");
      } else {
        await axios.post(`${API}/zip-routing`, submitData, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success("Zip routing created");
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({
        zip_code: "",
        primary_tech_id: "",
        fallback_tech_id: "",
        self_book_enabled: true,
      });
      fetchRoutings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save zip routing");
    }
  };

  const handleEdit = (routing) => {
    setEditingId(routing.routing_id);
    setFormData({
      zip_code: routing.zip_code,
      primary_tech_id: routing.primary_tech_id,
      fallback_tech_id: routing.fallback_tech_id || "",
      self_book_enabled: routing.self_book_enabled !== false,
    });
    setShowForm(true);
  };

  // Inline toggle from the row — flips self_book on/off without opening
  // the full edit form, so admins can quickly reroute an area.
  const handleToggleSelfBook = async (routing) => {
    const token = localStorage.getItem('session_token');
    const newVal = !(routing.self_book_enabled !== false);
    try {
      await axios.put(
        `${API}/zip-routing/${routing.routing_id}`,
        { self_book_enabled: newVal },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      toast.success(
        newVal
          ? `Self-booking ON for ${routing.zip_code}`
          : `Self-booking OFF for ${routing.zip_code} — customers must text to book`,
      );
      fetchRoutings();
    } catch (e) {
      toast.error("Failed to toggle");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/zip-routing/${deleteId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Zip routing deleted");
      setDeleteId(null);
      fetchRoutings();
    } catch (error) {
      toast.error("Failed to delete zip routing");
    }
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  if (user?.role !== "Admin") {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      <Header user={user} />
      
      <div className="max-w-md mx-auto pb-24 p-4">
        <div className="mb-6">
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-inter font-semibold">Back to Settings</span>
          </button>
          
          <h1 className="font-barlow text-4xl font-bold text-zinc-950 uppercase mb-2">Zip Code Routing</h1>
          <p className="font-inter text-zinc-600">Map zip codes to primary and fallback screen techs</p>
        </div>

        {!showForm ? (
          <button
            onClick={() => {
              setEditingId(null);
              setFormData({
                zip_code: "",
                primary_tech_id: "",
                fallback_tech_id: "",
                self_book_enabled: true,
              });
              setShowForm(true);
            }}
            data-testid="add-zip-routing-button"
            className="w-full h-12 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold flex items-center justify-center gap-2 mb-6"
          >
            <Plus className="w-5 h-5" />
            Add Zip Code
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border-2 border-zinc-100 p-5 mb-6">
            <h3 className="font-inter text-lg font-bold text-zinc-900 mb-4">
              {editingId ? "Edit Zip Routing" : "Add Zip Code Routing"}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="font-inter font-semibold text-zinc-700 block mb-2">Zip Code *</label>
                <input
                  type="text"
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  required
                  maxLength={5}
                  pattern="[0-9]{5}"
                  placeholder="94102"
                  disabled={!!editingId}
                  className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white"
                />
              </div>

              <div>
                <label className="font-inter font-semibold text-zinc-700 block mb-2">Primary Tech *</label>
                <select
                  value={formData.primary_tech_id}
                  onChange={(e) => setFormData({ ...formData, primary_tech_id: e.target.value })}
                  required
                  className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white"
                >
                  <option value="">Select primary tech</option>
                  {techs.map(tech => (
                    <option key={tech.tech_id} value={tech.tech_id}>{tech.name}</option>
                  ))}
                </select>
                <p className="text-sm text-zinc-500 mt-1">First choice for this zip code</p>
              </div>

              <div>
                <label className="font-inter font-semibold text-zinc-700 block mb-2">Fallback Tech</label>
                <select
                  value={formData.fallback_tech_id}
                  onChange={(e) => setFormData({ ...formData, fallback_tech_id: e.target.value })}
                  className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white"
                >
                  <option value="">No fallback</option>
                  {techs.filter(t => t.tech_id !== formData.primary_tech_id).map(tech => (
                    <option key={tech.tech_id} value={tech.tech_id}>{tech.name}</option>
                  ))}
                </select>
                <p className="text-sm text-zinc-500 mt-1">Used if primary tech is unavailable</p>
              </div>

              <div className="rounded-lg border-2 border-zinc-200 p-3 bg-zinc-50">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.self_book_enabled}
                    onChange={(e) => setFormData({ ...formData, self_book_enabled: e.target.checked })}
                    data-testid="self-book-enabled-checkbox"
                    className="w-5 h-5 mt-0.5 accent-[#207024] cursor-pointer"
                  />
                  <span className="flex-1">
                    <span className="font-inter font-semibold text-zinc-900 block">Allow online self-booking</span>
                    <span className="font-inter text-sm text-zinc-600 block mt-0.5">
                      When OFF, customers in this zip see a &quot;Text us to book&quot; CTA instead of live availability.
                    </span>
                  </span>
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                  }}
                  className="h-12 flex-1 rounded-lg bg-white border-2 border-zinc-200 text-zinc-900 font-semibold hover:bg-zinc-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="h-12 flex-1 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold"
                >
                  {editingId ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </form>
        )}

        <div className="space-y-4">
          {routings.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="font-inter text-zinc-500">No zip code routing configured</p>
              <p className="font-inter text-sm text-zinc-400 mt-1">Add zip codes to route customers to specific techs</p>
            </div>
          ) : (
            routings.map((routing) => (
              <div
                key={routing.routing_id}
                className="bg-white rounded-xl border-2 border-zinc-100 p-5"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-inter text-2xl font-bold text-[#207024] mb-2">
                      {routing.zip_code}
                    </h3>
                    <div className="space-y-1">
                      <p className="text-sm">
                        <span className="font-semibold text-zinc-700">Primary:</span>{" "}
                        <span className="text-zinc-900">{routing.primary_tech_name}</span>
                      </p>
                      {routing.fallback_tech_name && (
                        <p className="text-sm">
                          <span className="font-semibold text-zinc-700">Fallback:</span>{" "}
                          <span className="text-zinc-900">{routing.fallback_tech_name}</span>
                        </p>
                      )}
                      <p className="text-sm">
                        <span className="font-semibold text-zinc-700">Self-booking:</span>{" "}
                        {routing.self_book_enabled !== false ? (
                          <span className="text-[#207024] font-semibold">ON</span>
                        ) : (
                          <span className="text-orange-600 font-semibold">OFF — text to book</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleToggleSelfBook(routing)}
                  data-testid={`toggle-self-book-${routing.zip_code}`}
                  className={`w-full h-10 rounded-lg font-semibold mb-2 border-2 transition-colors ${
                    routing.self_book_enabled !== false
                      ? "bg-white border-zinc-200 text-zinc-900 hover:bg-zinc-50"
                      : "bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
                  }`}
                >
                  {routing.self_book_enabled !== false ? "Turn Self-Booking OFF" : "Turn Self-Booking ON"}
                </button>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(routing)}
                    className="h-10 flex-1 rounded-lg bg-white border-2 border-zinc-200 text-zinc-900 font-semibold hover:bg-zinc-50 flex items-center justify-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => setDeleteId(routing.routing_id)}
                    className="h-10 flex-1 rounded-lg bg-red-50 border-2 border-red-200 text-red-700 font-semibold hover:bg-red-100 flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Zip Routing</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this zip code routing? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
